/* eslint-disable jsx-a11y/no-autofocus */
import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ReactTooltip from 'react-tooltip'
import { Info } from '../assets'
import { useDispatch, useSelector } from 'react-redux'
import { nextSimulationStep } from 'store/simulation/actions/switchSimulationStep'
import NavigationButtons from '../components/navigation-buttons/NavigationButtons'
import { Store } from 'store/types'
import { updateSimulationInput } from 'store/simulation/actions/updateSimulationInput'
import { SimulationInputKeys } from 'store/simulation/types'
import { MoreInformationPopup } from '../components/more-information-popup/MoreInformationPopup'
import { trackEvent } from 'utils/eventTracking'
import styles from './Steps.module.scss'
import simulationStyles from '../Simulation.module.scss'
import classNames from 'classnames'

export const SolarPowerStep: FC = () => {
  // REDUX STORE
  const { inputs } = useSelector((state: Store) => state.simulation)
  const dispatch = useDispatch()

  // i18n
  const { t } = useTranslation('simulation')

  // STATE
  const [showInfo, setShowInfo] = useState(false)
  const [showPanels, setShowPanels] = useState(false)

  /**
   * Saves the solar panels input in the store
   *
   * @param solarPanels
   */
  const saveSolarPanelsInput = (solarPanels: number | null) => {
    dispatch(updateSimulationInput({ key: SimulationInputKeys.SOLAR_PANELS, value: solarPanels }))

    // Calculate solar power (in kva) based on given solar panels & save it
    const solarPower = (solarPanels || 0) * 245 * 0.85 * 0.001
    dispatch(updateSimulationInput({ key: SimulationInputKeys.SOLAR_POWER, value: solarPower }))
  }

  /**
   * Saves the solar power input in the store
   *
   * @param solarPower
   */
  const saveSolarPowerInput = (solarPower: number | null) => {
    dispatch(updateSimulationInput({ key: SimulationInputKeys.SOLAR_POWER, value: solarPower }))
    if (solarPower === 0) setShowPanels(true)
  }

  /**
   * Handles the simulation button click
   */
  const handleSimulationButtonClick = () => {
    // Track 'Simulation Started' event @Semetis
    trackEvent('simulation_started', {
      step: 'solar-power'
    })

    // Go to next step
    dispatch(nextSimulationStep())
  }

  return (
    <div>
      <div className="is-flex-column is-flex-centered">
        <h2 className={classNames(styles.title, 'margin-top-none', 'margin-bottom-l')}>
          {showPanels ? (
            <>
              {t('howManySolarPanels', 'Hoeveel zonnepanelen heb je?')}
              <Info
                className={simulationStyles['info-icon']}
                data-html
                data-for="tooltip-solarpanels"
                data-tip={t('solarPanelsTooltip', 'We schatten je vermogen in op basis van een gemiddeld zonnepaneel in België.')}
              />
              <ReactTooltip effect="solid" id="tooltip-solarpanels" uuid="tooltip-solarpanels-uuid" event="click" className="tooltip" />
            </>
          ) : (
            t('howMuchKvaTitle', 'Hoeveel kVA is het vermogen van de omvormer(s) van jouw installatie(s)? (in kVA)')
          )}
        </h2>
      </div>

      <div className={styles['simulation-inputs']}>
        {showPanels ? (
          <>
            <div>
              <input
                type="number"
                className="border"
                min="0"
                step="1"
                required
                autoFocus
                value={inputs.solarPanels}
                onChange={(e) => saveSolarPanelsInput(parseInt(e.currentTarget.value, 10) || null)}
              />
            </div>
            <div className="is-flex-row">
              <button className={classNames(simulationStyles.link, 'margin-right-s')} onClick={() => setShowPanels(false)}>
                {t('kvaKnown', 'Ik ken het vermogen van mijn installatie')}
              </button>
              <button
                className={classNames(simulationStyles.link, 'margin-left-s')}
                onClick={() => {
                  saveSolarPowerInput(4)
                  dispatch(nextSimulationStep())
                }}
              >
                {t('dontKnowSolarPower', 'Ik weet het niet')}
              </button>
            </div>
          </>
        ) : (
          <>
            <div>
              <input
                type="number"
                className="border"
                min="0"
                step=".01"
                required
                autoFocus
                value={inputs.solarPower}
                onChange={(e) => saveSolarPowerInput(parseFloat(e.target.value) || null)}
              />
            </div>

            {inputs.solarPower > 10 && (
              <div className="is-red has-text-centered margin-bottom-m">
                {t('moreThan10kva', 'In geval uw installatie meer dan 10kVA produceert dan moet u een apart injectiecontract afsluiten.')}
              </div>
            )}

            <div className="is-flex-row">
              <button className={classNames(simulationStyles.link, 'margin-right-s')} onClick={() => setShowInfo(true)}>
                {t('link', 'Meer informatie')}
              </button>

              <button className={classNames(simulationStyles.link, 'margin-left-s')} onClick={() => saveSolarPowerInput(0)}>
                {t('dontKnowSolarPower', 'Ik weet het niet')}
              </button>
            </div>
          </>
        )}
      </div>

      <NavigationButtons
        nextDisabled={(inputs.solarPower === null && inputs.solarPanels === null) || inputs.solarPower > 10}
        onNextClick={() => handleSimulationButtonClick()}
      />

      <MoreInformationPopup isOpen={showInfo} onRequestClose={() => setShowInfo(false)} i18nKey="howMuchKvaDialogContent" />
    </div>
  )
}
